import sseService from './sseService.js'
import packageInfo from '../../package.json'

// Versuche Build-Info zu laden (falls verfügbar)
let buildInfo = null
try {
  buildInfo = require('../build-info.json')
} catch (error) {
  // build-info.json nicht verfügbar (Development)
  console.log('Build info not available, using package.json info')
}

class MaintenanceService {
  constructor() {
    this.isMaintenanceMode = false
    this.listeners = []
    this.checkInterval = null
    this.baseURL = process.env.VUE_APP_BASE_URL
    this.useSSE = true // Flag für SSE vs. Polling
    this.fallbackInterval = null
    
    // App-Informationen für Server-Identifikation
    this.appName = packageInfo.name
    this.appVersion = packageInfo.version
    this.buildTimestamp = this.getBuildTimestamp()
    
    // Auth-Token
    this.currentAuthToken = null
  }

  // Hole Build-Timestamp
  getBuildTimestamp() {
    // Priorität: 1. build-info.json, 2. Umgebungsvariable, 3. aktueller Timestamp
    if (buildInfo && buildInfo.buildTimestamp) {
      return buildInfo.buildTimestamp
    }
    if (process.env.VUE_APP_BUILD_TIMESTAMP) {
      return process.env.VUE_APP_BUILD_TIMESTAMP
    }
    return new Date().toISOString()
  }

  // Hole App-Informationen für Server-Requests
  getAppInfo() {
    return {
      name: this.appName,
      version: this.appVersion,
      buildTimestamp: this.buildTimestamp,
      timestamp: new Date().toISOString()
    }
  }

  // Setze Auth-Token
  setAuthToken(authToken) {
    this.currentAuthToken = authToken
    console.log('Auth token set in MaintenanceService:', authToken ? '***' : 'none')
    
    // Aktualisiere SSE-Verbindung mit Auth-Token
    if (this.useSSE && sseService.isSSEConnected()) {
      sseService.updateWithAuthToken(authToken)
    }
  }

  // Hole Auth-Token
  getAuthToken() {
    return this.currentAuthToken
  }

  // Hole kombinierte App- und Auth-Informationen
  getFullInfo() {
    return {
      app: this.getAppInfo(),
      authToken: this.getAuthToken(),
      timestamp: new Date().toISOString()
    }
  }

  // Hole Headers mit App-Informationen
  getAppHeaders() {
    return {
      'X-App-Name': this.appName,
      'X-App-Version': this.appVersion,
      'X-App-Build-Timestamp': this.buildTimestamp,
      'X-Client-Timestamp': new Date().toISOString()
    }
  }


  // Füge einen Listener hinzu, der benachrichtigt wird, wenn sich der Maintenance-Status ändert
  addListener(callback) {
    this.listeners.push(callback)
  }

  // Entferne einen Listener
  removeListener(callback) {
    this.listeners = this.listeners.filter(listener => listener !== callback)
  }

  // Benachrichtige alle Listener über Statusänderungen
  notifyListeners() {
    this.listeners.forEach(callback => callback(this.isMaintenanceMode))
  }


  // Prüfe den Maintenance-Status vom Server
  async checkMaintenanceStatus() {
    try {
      const response = await fetch(`${this.baseURL}/agency/app/auth/maintenanceStatus`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        // Timeout nach 5 Sekunden
        signal: AbortSignal.timeout(5000)
      })

      if (!response.ok) {
        // Bei HTTP-Fehlern (500, etc.) Maintenance-Modus aktivieren
        this.setMaintenanceMode(true)
        return
      }

      const data = await response.json()
      
      // Prüfe den Status
      if (data.status === 'maintenance') {
        this.setMaintenanceMode(true)
      } else if (data.status === 'running') {
        this.setMaintenanceMode(false)
      } else {
        // Unbekannter Status - vorsichtshalber Maintenance-Modus
        this.setMaintenanceMode(true)
      }
    } catch (error) {
      // Bei Netzwerkfehlern oder Timeouts Maintenance-Modus aktivieren
      console.warn('Maintenance Status Check failed:', error)
      this.setMaintenanceMode(true)
    }
  }

  // Setze den Maintenance-Modus und benachrichtige Listener
  setMaintenanceMode(isMaintenance) {
    if (this.isMaintenanceMode !== isMaintenance) {
      this.isMaintenanceMode = isMaintenance
      this.notifyListeners()
    }
  }

  // Starte Real-time Überwachung (SSE mit Fallback)
  startPeriodicCheck() {
    if (this.useSSE) {
      this.startSSEMonitoring()
    } else {
      this.startPolling()
    }
  }

  // Starte SSE-basierte Überwachung
  startSSEMonitoring() {
    // SSE Listener hinzufügen
    sseService.addListener(this.handleSSEUpdate.bind(this))
    
    // SSE-Verbindung mit App-Informationen und Auth-Token starten
    sseService.connect(this.getAppInfo(), this.currentAuthToken)
    
    // Fallback-Polling starten (alle 60 Sekunden als Backup)
    this.startFallbackPolling()
    
    // Sofortige erste Prüfung
    this.checkMaintenanceStatus()
  }

  // Starte traditionelles Polling (Fallback)
  startPolling() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
    }
    
    // Sofortige erste Prüfung
    this.checkMaintenanceStatus()
    
    // Dann alle 30 Sekunden
    this.checkInterval = setInterval(() => {
      this.checkMaintenanceStatus()
    }, 30000)
  }

  // Starte Fallback-Polling (langsamer, als Backup für SSE)
  startFallbackPolling() {
    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval)
    }
    
    // Alle 60 Sekunden als Backup
    this.fallbackInterval = setInterval(() => {
      if (!sseService.isSSEConnected()) {
        console.log('SSE disconnected, using fallback polling')
        this.checkMaintenanceStatus()
      }
    }, 60000)
  }

  // Behandle SSE Updates
  handleSSEUpdate(data) {
    console.log('SSE maintenance update:', data)
    
    if (data.status === 'maintenance') {
      this.setMaintenanceMode(true)
    } else if (data.status === 'running') {
      this.setMaintenanceMode(false)
    }
  }

  // Stoppe periodische Überprüfung
  stopPeriodicCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
    }
    
    if (this.fallbackInterval) {
      clearInterval(this.fallbackInterval)
      this.fallbackInterval = null
    }
    
    // SSE-Verbindung trennen
    sseService.disconnect()
  }

  // Manuelle Prüfung (für Router Guards, etc.)
  async forceCheck() {
    // Wenn SSE verbunden ist, verwende SSE für manuelle Prüfung
    if (this.useSSE && sseService.isSSEConnected()) {
      const data = await sseService.checkStatusManually()
      this.handleSSEUpdate(data)
    } else {
      // Fallback zu traditioneller API-Abfrage
      await this.checkMaintenanceStatus()
    }
    return this.isMaintenanceMode
  }

  // Wechsle zwischen SSE und Polling-Modus
  setSSEMode(enabled) {
    this.useSSE = enabled
    
    if (enabled) {
      this.startSSEMonitoring()
    } else {
      sseService.disconnect()
      this.startPolling()
    }
  }

  // Prüfe ob SSE aktiv ist
  isSSEActive() {
    return this.useSSE && sseService.isSSEConnected()
  }

  // Getter für aktuellen Status
  getMaintenanceMode() {
    return this.isMaintenanceMode
  }

  // Methode für andere Services, um App-Informationen zu erhalten
  static getAppInfo() {
    return maintenanceService.getAppInfo()
  }

  // Methode für andere Services, um App-Headers zu erhalten
  static getAppHeaders() {
    return maintenanceService.getAppHeaders()
  }
}

// Singleton-Instanz
const maintenanceService = new MaintenanceService()

export default maintenanceService
